import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./ExtendedEphemeris.scss";
import InnerNav from "../components/InnerNav/InnerNav";
import Footer from "../components/Footer/Footer";

const ExtendedEphemeris = () => {
  const currentYear = new Date().getFullYear();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // selectors
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1);
  const [system, setSystem] = useState("lahiri");

  const [meta, setMeta] = useState({
    system: "",
    year: "",
    month: "",
    count: 0,
  });

  // accordion state
  const [openDate, setOpenDate] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      setOpenDate(null);

      const res = await axios.get(
        "https://core-c343.onrender.com/api/extended-ephemeris",
        {
          params: { year, month, system },
        }
      );

      setRows(res.data.data || []);
      setMeta({
        system: res.data.system,
        year: res.data.year,
        month: res.data.month,
        count: res.data.count,
      });
    } catch (err) {
      console.error(err);
      setRows([]);
      setError("Failed to load extended ephemeris");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= GROUP BY DATE ================= */
  const groupedByDate = useMemo(() => {
    return rows.reduce((acc, r) => {
      acc[r.date] = acc[r.date] || [];
      acc[r.date].push(r);
      return acc;
    }, {});
  }, [rows]);

  return (
    <div className="ext-ephemeris">
      <InnerNav />

      <div className="ext-content">
        <h2>Extended Ephemeris – Planetary Aspects</h2>

        {/* ================= FILTER BAR ================= */}
        <div className="ext-filters">
          <label>
            Year
            <select value={year} onChange={(e) => setYear(+e.target.value)}>
              {Array.from({ length: 11 }, (_, i) => currentYear - 5 + i).map(
                (y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                )
              )}
            </select>
          </label>

          <label>
            Month
            <select value={month} onChange={(e) => setMonth(+e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>
          </label>

          <label>
            System
            <select
              value={system}
              onChange={(e) => setSystem(e.target.value)}
            >
              <option value="lahiri">Sidereal (Lahiri)</option>
              <option value="tropical">Tropical (Western)</option>
            </select>
          </label>

          <button className="ext-submit" onClick={fetchData}>
            Submit
          </button>
        </div>

        {/* ================= META ================= */}
        {!loading && !error && meta.count > 0 && (
          <div className="ext-meta">
            <span><b>System:</b> {meta.system}</span>
            <span><b>Year:</b> {meta.year}</span>
            <span><b>Month:</b> {meta.month}</span>
            <span><b>Total Aspects:</b> {meta.count}</span>
          </div>
        )}

        {/* ================= STATES ================= */}
        {loading && <p className="ext-status">Loading ephemeris…</p>}
        {error && <p className="ext-status error">{error}</p>}

        {/* ================= TABLE (ACCORDION) ================= */}
        {!loading && !error && (
          <table className="ext-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Aspecting Planets</th>
                <th>Aspect Type</th>
                <th>1st Longitude</th>
                <th>2nd Longitude</th>
                <th>Distance</th>
              </tr>
            </thead>

            <tbody>
              {Object.entries(groupedByDate).map(([date, items]) => {
                const isOpen = openDate === date;

                return (
                  <React.Fragment key={date}>
                    {/* DATE HEADER */}
                    <tr
                      className="date-header"
                      onClick={() => setOpenDate(isOpen ? null : date)}
                    >
                      <td colSpan={7}>
                        <span className="arrow">
                          {isOpen ? "▼" : "▶"}
                        </span>
                        <strong>{date}</strong>
                        <span className="count">
                          ({items.length} aspects)
                        </span>
                      </td>
                    </tr>

                    {/* EXPANDED ROWS */}
                    {isOpen &&
                      items.map((r, i) => (
                        <tr key={i} className="aspect-row">
                          <td></td>
                          <td>{r.time}</td>
                          <td>{r.planets}</td>
                          <td>{r.aspect}</td>
                          <td>{r.lon1}</td>
                          <td>{r.lon2}</td>
                          <td>{r.distance}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ExtendedEphemeris;
